import get from 'lodash/get';
import includes from 'lodash/includes';

import { CancelToken } from '@wetransfer/axios';

import Config from './config';

import TransferError from '@app/helper/error';
import TransferErrorMessages from '@app/helper/error/messages';

import request from '@module/http/axios/request';
import retriableRequest from '@module/http/axios/retriable-request';
import { HttpResponseCodes } from '@module/http/axios/response';

const activeRequests = [];

export default {
  apiOptions: {
    ...Config.api,
  },

  configure(options = {}) {
    this.apiOptions = {
      ...this.apiOptions,
      ...options,
    };
  },

  request(method, path) {
    return request({
      path: `${this.apiOptions.endpoint}${path}`,
      method,
      timeout: this.apiOptions.timeout,
    });
  },

  retriableRequest(method, path, params = null, options = {}) {
    const retries = get(options, 'retries.limit', this.apiOptions.retry.limit);

    const source = CancelToken.source();
    activeRequests.push(source);

    return retriableRequest({
      path: `${this.apiOptions.endpoint}${path}`,
      method,
      timeout: this.apiOptions.retriableTimeout,
      params,
      cancelToken: source.token,
      retries,
    });
  },

  // This method will be used to abort all active API requests
  abort() {
    for (const source of activeRequests) {
      source.cancel('Transfer canceled by the user.');
    }
  },

  async fetchTransferId(type, params) {
    try {
      const response = await this.retriableRequest(
        'POST',
        `/transfers/${type}`,
        params
      );
      return response.body;
    } catch (error) {
      switch (error.status) {
        case HttpResponseCodes.LIMIT_REACHED:
          throw new TransferError(TransferErrorMessages.LIMIT_REACHED);
        case HttpResponseCodes.USER_BLOCKED:
          throw new TransferError(TransferErrorMessages.USER_BLOCKED);
        case HttpResponseCodes.UNAVAILABLE_PARENT:
          throw new TransferError(TransferErrorMessages.UNAVAILABLE_PARENT);
        case HttpResponseCodes.BAD_REQUEST:
          if (get(error, 'response.data.message') === 'too_many_recipients') {
            throw new TransferError(TransferErrorMessages.TOO_MANY_RECIPIENTS);
          }
          throw new TransferError(TransferErrorMessages.INVALID_TRANSFER);
        case HttpResponseCodes.EXPECTATION_FAILED:
          throw new TransferError(
            TransferErrorMessages.HUMAN_VERIFICATION_REQUIRED,
            {
              transferId: error.body.id,
            }
          );
        default:
          throw new TransferError(TransferErrorMessages.UNKNOWN_RESPONSE);
      }
    }
  },

  async validateRecaptcha(transferId, params) {
    try {
      const response = await this.retriableRequest(
        'POST',
        `/transfers/${transferId}/human_verification`,
        params
      );
      return response.body;
    } catch (error) {
      switch (error.status) {
        case HttpResponseCodes.BAD_REQUEST:
          throw new TransferError(TransferErrorMessages.RECAPTCHA_FAILED);
        default:
          throw new TransferError(TransferErrorMessages.UNKNOWN_RESPONSE);
      }
    }
  },

  async fetchTransfer(
    transferId,
    secret,
    recipientId = false,
    password = false,
    retry = true
  ) {
    const requestPath = `/transfers/${transferId}/prepare-download`;
    const options = {};
    const params = {
      security_hash: secret,
    };

    if (recipientId) {
      params.recipient_id = recipientId;
    }

    if (password) {
      params.password = password;
    }

    if (retry === false) {
      options.retries = 1;
    }

    try {
      const response = await this.retriableRequest(
        'POST',
        requestPath,
        params,
        options
      );
      return response.body;
    } catch (error) {
      switch (error.status) {
        case HttpResponseCodes.FORBIDDEN:
          throw new TransferError(TransferErrorMessages.ACCESS_DENIED);
        default:
          throw new TransferError(TransferErrorMessages.UNKNOWN_RESPONSE);
      }
    }
  },

  async fetchTransferDownload(
    transferId,
    secret,
    recipientId = false,
    fileEntries = false,
    password = false,
    domain_user_id = false
  ) {
    const path = `/transfers/${transferId}/download`;
    const params = {
      security_hash: secret,
    };

    if (recipientId) {
      params.recipient_id = recipientId;
    }

    if (password) {
      params.password = password;
    }

    if (fileEntries) {
      params.file_ids = fileEntries;
    }

    if (domain_user_id) {
      params.domain_user_id = domain_user_id;
    }

    try {
      const response = await this.retriableRequest('POST', path, params);
      return response.body;
    } catch (error) {
      switch (error.status) {
        case HttpResponseCodes.TOO_MANY_REQUESTS:
          throw new TransferError(TransferErrorMessages.TOO_MANY_REQUESTS);
        default:
          throw new TransferError(TransferErrorMessages.INVALID_TRANSFER);
      }
    }
  },

  async fetchFileObjectForm(transferId, fileObjectId, params, retries = true) {
    try {
      const response = await this.retriableRequest(
        'POST',
        `/transfers/${transferId}/files/${fileObjectId}/part-put-url`,
        params,
        { retries }
      );
      return response.body;
    } catch (error) {
      if (includes(error.message, 'timeout')) {
        throw new TransferError(TransferErrorMessages.UNKNOWN_RESPONSE);
      }

      switch (error.status) {
        case HttpResponseCodes.BAD_REQUEST:
          throw new TransferError(TransferErrorMessages.INVALID_TRANSFER);
        case HttpResponseCodes.FORBIDDEN:
          throw new TransferError(TransferErrorMessages.ACCESS_DENIED);
        default:
          throw new TransferError(TransferErrorMessages.UNKNOWN_RESPONSE);
      }
    }
  },

  async cancelTransfer(transferId) {
    const response = await this.request('DELETE', `/transfers/${transferId}`);
    return response.body;
  },

  async exitTransfer(transferId) {
    const response = await this.retriableRequest(
      'PUT',
      `/transfers/${transferId}/browser_exit`
    );
    return response.body;
  },

  async finalizeTransfer(transferId) {
    try {
      const response = await this.retriableRequest(
        'PUT',
        `/transfers/${transferId}/finalize`
      );
      return response.body;
    } catch (error) {
      switch (error.status) {
        case HttpResponseCodes.BAD_REQUEST:
          throw new TransferError(TransferErrorMessages.INVALID_TRANSFER);
        case HttpResponseCodes.FORBIDDEN:
          throw new TransferError(TransferErrorMessages.ACCESS_DENIED);
        default:
          throw new TransferError(TransferErrorMessages.UNKNOWN_RESPONSE);
      }
    }
  },
};
